// src/pages/TextTools/LitBot.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getLitBotReferences, askLitBot } from '../../services/chatApi';
import { Send, BookOpen, Loader2, Menu, X } from 'lucide-react';
import Markdown from 'react-markdown';
import { LitBot_PROMPTS } from './liBotPrompts';
import { useAuth } from '../../contexts/AuthContext'; // To check for credits
import { Link } from 'react-router-dom';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};


const LitBot = () => {
    const { t, i18n } = useTranslation();
    const { user, isAuthenticated, setCredits } = useAuth();

    const [references, setReferences] = useState([]);
    const [isLoadingReferences, setIsLoadingReferences] = useState(true);
    const [messages, setMessages] = useState([]);
    const [userInput, setUserInput] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState(null);
    const [showReferences, setShowReferences] = useState(false);

    const chatContainerRef = useRef(null);

    // Fetch references on component mount
    useEffect(() => {
        const fetchReferences = async () => {
            try {
                const data = await getLitBotReferences();
                setReferences(data);
            } catch (err) {
                setError(t('litbot.errorFetchingReferences'));
                console.error(err);
            } finally {
                setIsLoadingReferences(false);
            }
        };
        fetchReferences();
    }, [t]);

    // Auto-scroll chat
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!userInput.trim() || isSending) return;

        const newUserMessage = { role: 'user', content: userInput };
        setMessages(prev => [...prev, newUserMessage]);
        setUserInput('');
        setIsSending(true);
        setError(null);

        // Format history for the API
        let historyForApi = messages
            .filter(m => m.role !== 'system')
            .reduce((acc, msg, i) => {
                if (msg.role === 'user') {
                    const nextMsg = messages[i + 1];
                    if (nextMsg && nextMsg.role === 'assistant') {
                        acc.push([msg.content, nextMsg.content]);
                    }
                }
                return acc;
            }, []);

        let systemPromptForApi = null;

        const language = i18n.language.split('-')[0];
        const prompts = LitBot_PROMPTS[language] || LitBot_PROMPTS['en'];
        systemPromptForApi = prompts.system_prompt;


        try {
            const result = await askLitBot(userInput, historyForApi, systemPromptForApi);
            const newBotMessage = {
                role: 'assistant',
                content: result.answer,
                citations: result.citations || [],
            };
            setMessages(prev => [...prev, newBotMessage]);

            if (result.new_credits !== undefined) {
                setCredits(result.new_credits);
            }
        } catch (err) {
            setError(err.message || t('litbot.errorAnswering'));
            const errorBotMessage = {
                role: 'assistant',
                content: t('litbot.errorAnswering'),
                isError: true,
            };
            setMessages(prev => [...prev, errorBotMessage]);
        } finally {
            setIsSending(false);
        }
    };

    const showSubscribePrompt = user?.subscription_status !== 'active';
    const showPurchaseCreditsPrompt = user?.subscription_status === 'active' && user?.credits <= 0;

    return (
    <div className="flex h-full overflow-hidden bg-brand-white text-brand-dark relative">
        {/* Left Panel: Chat Interface (was Right Panel) */}
        <div className={`${showReferences ? 'hidden md:flex md:w-1/2' : 'w-full md:w-1/2'} flex flex-col h-full transition-all duration-300`}>
            {showSubscribePrompt ? (
                <SubscribePrompt />
            ) : showPurchaseCreditsPrompt ? (
                <PurchaseCreditsPrompt />
            ) : (
                <>
                    <div ref={chatContainerRef} className="flex-1 p-6 overflow-y-auto">
                        {messages.length === 0 && (
                            <div className="text-left text-brand-dark h-full flex flex-col justify-center">
                                <h2 className="text-2xl font-bold text-brand-orange">{t('litbot.welcomeTitle')}</h2>
                                <p>{t('litbot.welcomeMessage')}</p>
                            </div>
                        )}
                        <div className="space-y-4">
                            {messages.map((msg, index) => (
                                <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-xl p-3 rounded-lg ${msg.role === 'user' ? 'bg-brand-orange text-white' : 'bg-slate-200 text-brand-dark'}`}>
                                        <div className="prose">
                                            <Markdown>{msg.content}</Markdown>
                                        </div>
                                        {msg.citations && msg.citations.length > 0 && (
                                            <div className="mt-3 pt-3 border-t border-slate-300">
                                                <h4 className="font-bold text-xs mb-1">{t('litbot.sourcesTitle')}</h4>
                                                <ul className="list-disc list-inside text-xs space-y-1">
                                                    {msg.citations.map((cite, i) => <li key={i}>{cite}</li>)}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {isSending && (
                                <div className="flex justify-start">
                                   <div className="max-w-xl p-3 rounded-lg bg-slate-200">
                                        <Loader2 className="animate-spin text-brand-dark" />
                                   </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="p-4">
                        <form onSubmit={handleSubmit} className="flex items-center space-x-2">
                            <input
                                type="text"
                                value={userInput}
                                onChange={(e) => setUserInput(e.target.value)}
                                placeholder={t('litbot.chatPlaceholder')}
                                className="flex-1 p-2 bg-brand-white text-brand-dark border border-slate-400 rounded-md focus:ring-1 focus:ring-brand-orange focus:outline-none"
                                disabled={isSending}
                            />
                            <button
                                type="submit"
                                className="p-2 bg-brand-orange text-white rounded-md hover:bg-orange-600 disabled:bg-slate-400"
                                disabled={isSending || !userInput.trim()}
                            >
                                <Send size={20} />
                            </button>
                        </form>
                        {error && <p className="text-red-500 mt-2 text-sm">{error}</p>}

                        {/* Toggle button for mobile to show/hide references */}
                        <button
                            className="mt-4 w-full p-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 md:hidden flex items-center justify-center gap-2"
                            onClick={() => setShowReferences(true)} // Always set to true to show
                          >
                              <BookOpen size={20} />
                              <span>{t('litbot.showReferencesButton')}</span>
                          </button>
                    </div>
                </>
            )}
        </div>

        {/* Right Panel: References (was Left Panel) - Always visible */}
        <div className={`${showReferences ? 'fixed inset-0 z-50 md:relative md:inset-auto' : 'hidden'} md:flex md:w-1/2 bg-brand-dark text-brand-white border-l border-gray-700 flex-col transition-all duration-300`}>
        <div className="p-4 overflow-y-auto flex-1">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-bold text-white flex items-center">
              <BookOpen className="mr-2" />
              {t('litbot.referencesTitle')}
            </h3>
            <button
              className="md:hidden text-white hover:text-orange-500"
              onClick={() => setShowReferences(false)}
            >
              <X size={24} />
            </button>
          </div>
          <ul className="space-y-4">
            {references.map(ref => (
              <li key={ref.id} className="text-sm">
                <span className="font-semibold">{ref.authors} ({ref.year}).</span>
                <em className="ml-1">{ref.title},</em>
                <em className="ml-1">{ref.publisher}.</em>
                <a 
                  className="ml-1 underline hover:no-underline hover:text-orange-500 transition-colors" 
                  href={ref.country}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Link
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Backdrop overlay for mobile */}
      {showReferences && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setShowReferences(false)}
        />
      )}
    </div>
);

};

export default LitBot;